import { useEffect, useState, useCallback } from "react";
import { Plus, Trash, PaperPlaneTilt, Robot, PencilSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { SortHeader } from "../components/SortHeader";
import { useTableSort } from "../lib/useTableSort";

const emptyRec = { id: null, name: "", chat_id: "", notify_punch: false, notify_price: false, notify_invoice: false };

export default function AdminNotifications() {
  const [token, setToken] = useState("");
  const [recipients, setRecipients] = useState([]);
  const recSort = useTableSort(recipients, "name", "asc");
  const [form, setForm] = useState(emptyRec);
  const [showForm, setShowForm] = useState(false);
  const [savingToken, setSavingToken] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [tRes, rRes] = await Promise.all([
        api.get("/notifications/bot-token"),
        api.get("/notifications/recipients"),
      ]);
      setToken(tRes.data.token || "");
      setRecipients(rRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveToken = async () => {
    setSavingToken(true);
    try {
      await api.put("/notifications/bot-token", { token: token.trim() });
      toast.success("Token guardado");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingToken(false); }
  };

  const sendTest = async () => {
    try {
      await api.post("/notifications/test");
      toast.success("Mensaje de prueba enviado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/notifications/recipients/${form.id}`, form);
      } else {
        await api.post("/notifications/recipients", form);
      }
      toast.success("Guardado");
      setShowForm(false);
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Eliminar destinatario ${r.name}?`)) return;
    try {
      await api.delete(`/notifications/recipients/${r.id}`);
      toast.success("Eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startEdit = (r) => { setForm({ ...r }); setShowForm(true); };
  const startCreate = () => { setForm(emptyRec); setShowForm(true); };

  return (
    <div>
      <div className="mb-8">
        <div className="label-cap">notificaciones</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Avisos por Telegram</h1>
      </div>

      <div className="card-flat p-6 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <Robot size={22} weight="duotone" color="#4A5D23" />
          <div className="font-display font-bold text-lg">Bot de Telegram</div>
        </div>
        <div className="text-sm text-[var(--text-secondary)] mb-3">
          1) Abre <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" className="text-[var(--primary)] underline">@BotFather</a> en Telegram y crea un bot con <code>/newbot</code>.<br/>
          2) Copia el <b>token</b> que te da (formato <code>1234567:AAA...</code>) y pégalo abajo.<br/>
          3) Añade el bot al grupo de Telegram, o pídele al bot que envíe primero un mensaje directo.<br/>
          4) Para obtener el <b>chat_id</b>: manda un mensaje al bot (o en el grupo con el bot) y abre <br/>
          <code className="break-all">https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code> — busca <code>{'"chat":{"id": ...}'}</code>.
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="bot-token"
            placeholder="1234567:AAA..."
            className="flex-1 min-w-[240px] px-4 py-3 rounded-xl border border-[var(--border)] bg-white font-mono text-sm"
          />
          <button onClick={saveToken} disabled={savingToken} data-testid="save-token" className="btn-primary px-5 py-3 rounded-xl font-display font-bold disabled:opacity-50">
            {savingToken ? "Guardando…" : "Guardar token"}
          </button>
          <button onClick={sendTest} data-testid="send-test" className="px-5 py-3 rounded-xl border border-[var(--border)] font-display font-bold flex items-center gap-2 hover:bg-white">
            <PaperPlaneTilt size={16} weight="duotone" /> Enviar prueba
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="font-display font-bold text-lg">Destinatarios</div>
        <button onClick={startCreate} data-testid="add-recipient" className="btn-primary px-4 py-2 rounded-xl font-display font-bold flex items-center gap-2">
          <Plus size={16} weight="bold" /> Nuevo destinatario
        </button>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--border)] bg-[var(--bg)]">
              <th className="text-left p-3"><SortHeader sortKey="name" currentKey={recSort.sortKey} currentDir={recSort.sortDir} onClick={recSort.toggleSort}>Nombre</SortHeader></th>
              <th className="text-left p-3"><SortHeader sortKey="chat_id" currentKey={recSort.sortKey} currentDir={recSort.sortDir} onClick={recSort.toggleSort}>Chat ID</SortHeader></th>
              <th className="text-center p-3"><SortHeader align="center" sortKey="notify_punch" currentKey={recSort.sortKey} currentDir={recSort.sortDir} onClick={recSort.toggleSort}>Fichajes</SortHeader></th>
              <th className="text-center p-3"><SortHeader align="center" sortKey="notify_price" currentKey={recSort.sortKey} currentDir={recSort.sortDir} onClick={recSort.toggleSort}>Precios</SortHeader></th>
              <th className="text-center p-3"><SortHeader align="center" sortKey="notify_invoice" currentKey={recSort.sortKey} currentDir={recSort.sortDir} onClick={recSort.toggleSort}>Facturas</SortHeader></th>
              <th className="text-right p-3 label-cap">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {recipients.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-[var(--text-secondary)]">Añade destinatarios (personas o grupo).</td></tr>
            )}
            {recSort.sortedRows.map((r) => (
              <tr key={r.id} className="border-b border-[var(--border)] last:border-0">
                <td className="p-3 font-display font-semibold">{r.name}</td>
                <td className="p-3 font-mono text-sm text-[var(--text-secondary)]">{r.chat_id}</td>
                <td className="p-3 text-center">{r.notify_punch ? "✅" : "—"}</td>
                <td className="p-3 text-center">{r.notify_price ? "✅" : "—"}</td>
                <td className="p-3 text-center">{r.notify_invoice ? "✅" : "—"}</td>
                <td className="p-3">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => startEdit(r)} className="p-2 rounded-lg border border-[var(--border)] hover:bg-white"><PencilSimple size={14} /></button>
                    <button onClick={() => del(r)} className="p-2 rounded-lg border border-[var(--border)] hover:bg-[var(--danger)] hover:text-white hover:border-[var(--danger)]"><Trash size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-flat w-full max-w-md p-8 relative">
            <button onClick={() => setShowForm(false)} className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-[var(--border)] flex items-center justify-center"><X size={18} /></button>
            <h2 className="font-display text-2xl font-bold tracking-tight mb-6">{form.id ? "Editar" : "Nuevo"} destinatario</h2>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="label-cap block mb-2">Nombre</label>
                <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="rec-name" className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-white" />
              </div>
              <div>
                <label className="label-cap block mb-2">Chat ID</label>
                <input required value={form.chat_id} onChange={(e) => setForm((f) => ({ ...f, chat_id: e.target.value }))} data-testid="rec-chat-id" className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-white font-mono" placeholder="-100123456789 (grupo) o 123456789 (persona)" />
              </div>
              <div className="space-y-2 pt-2">
                {[
                  { k: "notify_punch", label: "Aviso de fichajes (entrada/salida y horas trabajadas)" },
                  { k: "notify_price", label: "Alarma de variación de precio en artículos" },
                  { k: "notify_invoice", label: "Aviso de factura añadida (proveedor, nº, importe)" },
                ].map(({ k, label }) => (
                  <label key={k} className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-[var(--border)]">
                    <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.checked }))} data-testid={`rec-${k}`} className="w-5 h-5 accent-[var(--primary)]" />
                    <span className="text-sm">{label}</span>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-5 py-3 rounded-xl border border-[var(--border)] font-display font-semibold">Cancelar</button>
                <button type="submit" disabled={saving} className="btn-primary px-6 py-3 rounded-xl font-display font-bold disabled:opacity-50">{saving ? "…" : "Guardar"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
